import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async'; // Import Helmet and HelmetProvider
import { NavBar } from './components/NavBar/NavBar';
import { Footer } from './components/Footer/Footer';
import './index.css';
import { FindARep } from './pages/FindARep/FindARep';
import { CompareReps } from './pages/CompareReps/CompareReps';
import { HomePage } from './pages/HomePage/HomePage';
import { Dev } from './pages/Dev/Dev';
import { Login } from './pages/Login/Login';
import { Signup } from './pages/Signup/Signup';
import { Profile } from './pages/Profile/Profile';
import { PickYourViews } from './pages/PickYourViews/PickYourViews';
import { RepPage } from './pages/RepPage/RepPage';
import { About } from './pages/About/About';
import { Quiz } from './pages/Quiz/Quiz';
import { PrivacyPolicy } from './pages/PrivacyPolicy/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService/TermsOfService';
import './styleguide.css';
import './utils/firebase'; // Ensure this import is present to initialize Firebase
import { Analytics } from './pages/Analytics/Analytics';
import { Blog } from './pages/Blog/Blog';
// import { useDynamicMetaTags } from './hooks/useDynamicMetaTags'; // Remove unused hook import
import { createOrGetUser } from './utils/auth';

// Define meta tag data for different routes
const routeMetaMap = {
  '/': {
    title: 'Congressional voting data simplified | VoteSee',
    description: 'Visualize and analyze voting trends with VoteSee\'s interactive charts and analytics tools.',
  },
  '/find-a-rep': {
    title: 'Find Your Representative | VoteSee',
    description: 'Easily find your congressional representative and view their voting record.',
    image: '/images/previews/find-rep-preview.png',
  },
  '/compare-reps': {
    title: 'Compare Representatives | VoteSee',
    description: 'Compare the voting records and alignment of different representatives.',
    image: '/images/previews/compare-reps-preview.png',
  },
  '/quiz': {
    title: 'Political Alignment Quiz | VoteSee',
    description: 'Take our quiz to see how your views align with congressional representatives.',
    image: '/images/previews/quiz-preview.png',
  },
  '/pick-your-views': {
    title: 'Pick Your Views | VoteSee',
    description: 'Select the issues most important to you and see how representatives voted.',
    image: '/images/previews/pick-views-preview.png',
  },
  '/analytics': {
    title: 'Congressional Analytics | VoteSee',
    description: 'Explore in-depth analytics and visualizations of congressional voting data.',
    image: '/images/analytics/og-image.png', // Specific analytics image
  },
  // Add other routes as needed
};

const AppContent = () => {
  const [email, setEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  // Determine current meta tags based on route
  const currentPath = location.pathname;
  // Handle dynamic routes like /analytics/:chartType by checking the base path
  const baseAnalyticsPath = '/analytics';
  const isAnalyticsRoute = currentPath.startsWith(baseAnalyticsPath);
  const metaData = routeMetaMap[isAnalyticsRoute ? baseAnalyticsPath : currentPath] || routeMetaMap['/']; // Default to home if no match
  const canonicalUrl = `https://www.votesee.org${currentPath}`; // Assuming base URL

  useEffect(() => {
    const initUser = async () => {
      try {
        const user = await createOrGetUser();
        setEmail(user.email);
      } catch (error) {
        console.error('Error initializing user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initUser();
  }, []);

  const hideHeaderFooterRoutes = ['/landing-page'];
  const shouldHideHeaderFooter = hideHeaderFooterRoutes.includes(location.pathname);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Helmet>
        <title>{metaData.title}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={metaData.description} />
        {/* Open Graph Tags */}
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:image" content={`https://www.votesee.org${metaData.image}`} />
        <meta property="og:image:secure_url" content={`https://www.votesee.org${metaData.image}`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="VoteSee" />
        {/* Assuming standard OG image size */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaData.title} />
        <meta name="twitter:description" content={metaData.description} />
        <meta name="twitter:image" content={`https://www.votesee.org${metaData.image}`} />
        <meta name="twitter:site" content="@votesee" /> {/* Optional: Add your Twitter handle */}
      </Helmet>
      {!shouldHideHeaderFooter && <NavBar />}
      <main className="main-content">
        <Switch>
          <Route path="/rep/:politicianId" render={(props) => <RepPage {...props} email={email} />} />
          <Route path="/compare-reps" render={(props) => <CompareReps {...props} email={email} />} />
          <Route path="/find-a-rep" exact render={() => <FindARep email={email} />} />
          <Route path="/pick-your-views" exact render={() => <PickYourViews email={email} />} />
          <Route path="/dev" exact component={Dev} />
          <Route path="/login" exact component={Login} />
          <Route path="/signup" exact component={Signup} />
          <Route path="/profile" exact component={Profile} />
          <Route path="/quiz" exact component={Quiz} />
          <Route path="/analytics" exact component={Analytics} />
          <Route path="/analytics/:chartType" component={Analytics} />
          <Route path="/blog" exact component={Blog} />
          <Route path="/" exact component={HomePage} />
          <Route path="/about" exact component={About} />
          <Route path="/privacy-policy" exact component={PrivacyPolicy} />
          <Route path="/terms-of-service" exact component={TermsOfService} />
        </Switch>
      </main>
      {!shouldHideHeaderFooter && <Footer />}
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
